import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { aiirWorkspace } from '../../domain/aiirChecks';
import { downloadCsv } from '../../lib/csv';
import { date, daysBetween, DEMO_TODAY, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useToast } from '../../ui/feedback';
import { SectionCard, StatCard, StatGrid, StatusChip } from '../../ui/primitives';

const STEPS = ['Built from payments', 'Checks', 'Accountant review', 'Lodged'];
const STEP_INDEX = { NOT_STARTED: 0, DRAFT: 1, IN_REVIEW: 2, VALIDATED: 3, LODGED: 4 } as const;
const ACTION: Partial<Record<keyof typeof STEP_INDEX, string>> = {
  NOT_STARTED: 'Start the report',
  DRAFT: 'Send to the accountants for review',
  IN_REVIEW: 'Record accountant sign-off',
  VALIDATED: 'Lodge with the ATO',
};

export default function AiirWorkspace() {
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const reports = state.atoReports.filter((report) => report.kind === 'AIIR');
  const [reportId, setReportId] = useState(
    () => reports.find((report) => report.status !== 'LODGED')?.id ?? reports[0]?.id,
  );
  const report = reports.find((item) => item.id === reportId);
  const workspace = useMemo(
    () => (report ? aiirWorkspace(state, report.period) : undefined),
    [state, report],
  );
  if (!report || !workspace) return null;
  const { records, checks, byFund, reconciliation } = workspace;
  const days = daysBetween(DEMO_TODAY, report.dueDate);
  const fixes = checks.filter((check) => check.severity === 'FIX' && check.count > 0);
  const code = (fundId: string) => state.funds.find((fund) => fund.id === fundId)?.code ?? fundId;

  return (
    <SectionCard
      title="Annual investment income report (AIIR)"
      subtitle="Built from every payment on the register, checked, then reviewed by the accountants"
      action={
        <TextField
          select
          size="small"
          value={reportId}
          onChange={(event) => setReportId(event.target.value)}
          sx={{ minWidth: 140 }}
        >
          {reports.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.period}
            </MenuItem>
          ))}
        </TextField>
      }
      sx={{ mb: 2 }}
    >
      <Stepper activeStep={STEP_INDEX[report.status]} alternativeLabel sx={{ mb: 2 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <StatGrid min={160}>
        <StatCard label="Payee records" value={records.length} />
        <StatCard label="Income reported" value={money(reconciliation.reported)} />
        <StatCard
          label="Tax withheld"
          value={money(
            records.reduce((sum, record) => sum + record.tfnWithheld + record.nrWithheld, 0),
          )}
        />
        <StatCard
          label="Due"
          value={date(report.dueDate)}
          hint={
            report.status === 'LODGED'
              ? `Lodged · ${report.receipt}`
              : days >= 0
                ? `${days} days`
                : 'Overdue'
          }
          tone={report.status !== 'LODGED' && days < 30 ? 'warning' : 'neutral'}
        />
      </StatGrid>
      {reconciliation.fromLedger ? (
        <Alert
          severity={
            Math.abs(reconciliation.reported - reconciliation.ledger) < 1 ? 'success' : 'warning'
          }
          sx={{ mt: 2 }}
        >
          {Math.abs(reconciliation.reported - reconciliation.ledger) < 1
            ? `Reconciles to the ledger: ${money(reconciliation.ledger)} of distributions and tax withheld in ${report.period}.`
            : `Differs from the ledger by ${money(reconciliation.reported - reconciliation.ledger)}. Check before sending for review.`}
          {report.status === 'NOT_STARTED'
            ? ' The report keeps building as the year’s distributions are paid.'
            : ''}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          {report.period} was before the register moved to the platform, so it is built from the
          payment history migrated from Registry Direct.
        </Alert>
      )}
      <Box
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mt: 2 }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Checks
          </Typography>
          {checks.map((check) => (
            <Box key={check.key} sx={{ mb: 1.25 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <StatusChip
                  status={check.key}
                  label={String(check.count)}
                  tone={
                    check.count === 0 ? 'success' : check.severity === 'FIX' ? 'warning' : 'neutral'
                  }
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {check.label}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {check.detail}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            By fund
          </Typography>
          {byFund.map((row) => (
            <Typography key={row.fundId} variant="body2" sx={{ mb: 0.5 }}>
              {code(row.fundId)} · {row.payees} payees · {money(row.gross)}
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            Investment type is recorded once per fund at setup. The right type for each trust is to
            confirm with the accountants.
          </Typography>
        </Box>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {fixes.length > 0 && report.status !== 'LODGED' && (
          <Button
            variant="outlined"
            onClick={() => toast(`Requests sent to ${fixes[0].count} investors in the app`, 'info')}
          >
            Ask investors for missing details
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={() =>
            downloadCsv(`aiir_${report.period}_review.csv`, [
              [
                'Payee',
                'Fund',
                'TFN/ABN',
                'Residency',
                'Gross',
                'TFN withheld',
                'NR withheld',
                'Check',
              ],
              ...records.map((record) => [
                record.payeeName,
                code(record.fundId),
                record.identifier,
                record.residency,
                record.gross.toFixed(2),
                record.tfnWithheld.toFixed(2),
                record.nrWithheld.toFixed(2),
                record.warning ?? '',
              ]),
            ])
          }
        >
          Download review pack (CSV)
        </Button>
        {ACTION[report.status] && (
          <Button
            variant="contained"
            onClick={() => {
              dispatch({ type: 'ATO_REPORT_ADVANCE', id: report.id });
              toast(
                report.status === 'VALIDATED'
                  ? 'Lodged with the ATO; receipt recorded'
                  : 'Report updated',
              );
            }}
          >
            {ACTION[report.status]}
          </Button>
        )}
      </Stack>
    </SectionCard>
  );
}
